import { useState, useRef, useCallback, useEffect } from "react";

// ---------------------------------------------------------------------------
// Native platform detection + plugin helpers
// ---------------------------------------------------------------------------
const isNativePlatform = () => {
  try {
    return !!(
      window.Capacitor &&
      window.Capacitor.isNativePlatform &&
      window.Capacitor.isNativePlatform()
    );
  } catch (e) {
    return false;
  }
};

// Native audio recorder — uses Android's MediaRecorder directly, bypassing
// WebRTC's voice-call-optimized pipeline entirely.  Same quality as the
// built-in camera app.  Only available on native (Capacitor) platform.
let nativeRecorderPlugin = null;
const getNativeRecorderPlugin = () => {
  if (nativeRecorderPlugin) return nativeRecorderPlugin;
  try {
    if (isNativePlatform()) {
      nativeRecorderPlugin = window.Capacitor.registerPlugin(
        "NativeAudioRecorder",
      );
      return nativeRecorderPlugin;
    }
  } catch (e) {
    // Not on native platform — ignore
  }
  return null;
};

// Audio routing enforcement (still needed for web-path on Android WebView)
let audioRoutingPlugin = null;
const getAudioRoutingPlugin = () => {
  if (audioRoutingPlugin) return audioRoutingPlugin;
  try {
    if (isNativePlatform()) {
      audioRoutingPlugin = window.Capacitor.registerPlugin("AudioRouting");
      return audioRoutingPlugin;
    }
  } catch (e) {
    // Not on native platform — ignore
  }
  return null;
};

const startAudioRouteEnforcement = async () => {
  try {
    const plugin = getAudioRoutingPlugin();
    if (plugin) {
      const result = await plugin.startEnforcing();
      console.log("🔊 Audio route enforcement started:", result);
    }
  } catch (e) {
    console.warn("🔊 Audio route enforcement failed to start:", e);
  }
};

const stopAudioRouteEnforcement = async () => {
  try {
    const plugin = getAudioRoutingPlugin();
    if (plugin) {
      await plugin.stopEnforcing();
      console.log("🔊 Audio route enforcement stopped");
    }
  } catch (e) {
    // Plugin not available — ignore
  }
};

/**
 * Hook for recording audio.
 *
 * On native Android (Capacitor): uses NativeAudioRecorder plugin which records
 * through Android's MediaRecorder with VOICE_RECOGNITION source — no WebRTC
 * pipeline, no audio mode fighting, camera-app quality.
 *
 * On web: uses MediaRecorder API via getUserMedia (WebRTC path).
 *
 * Provides recording state, controls, and live analyser data for waveform
 * visualization.  UI-agnostic — can be used by song cards, notepad, or
 * teleprompter.
 */
const useAudioRecorder = ({
  durationLimitSec = 120,
  onDurationWarning,
} = {}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [error, setError] = useState(null);
  const [permissionGranted, setPermissionGranted] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const pausedDurationRef = useRef(0);
  const analyserRef = useRef(null);
  const audioContextRef = useRef(null);
  const warningFiredRef = useRef(false);

  // Native recording refs
  const useNativeRef = useRef(false);
  const nativeAmplitudeRef = useRef(0);
  const nativeListenerRef = useRef(null);

  // Ref for the native stop handler (shared between manual stop & auto-stop)
  const nativeStopFnRef = useRef(null);

  // -------------------------------------------------------------------------
  // Web-only: built-in mic constraints
  // -------------------------------------------------------------------------
  const getBuiltInMicConstraints = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter((d) => d.kind === "audioinput");

      const btKeywords = /bluetooth|bt |wireless|airpod|headset|hands.?free/i;
      const builtInKeywords = /built.?in|phone|bottom|back|internal|default/i;

      let builtIn = audioInputs.find(
        (d) => builtInKeywords.test(d.label) && !btKeywords.test(d.label),
      );
      if (!builtIn) {
        builtIn = audioInputs.find((d) => !btKeywords.test(d.label));
      }

      if (builtIn && builtIn.deviceId) {
        console.log(
          "🎤 Using built-in mic:",
          builtIn.label || builtIn.deviceId,
        );
        return {
          audio: {
            deviceId: { exact: builtIn.deviceId },
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
            sampleRate: { ideal: 44100 },
          },
        };
      }
    } catch (e) {
      // enumerateDevices not supported or failed — fall through
    }
    return {
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        sampleRate: { ideal: 44100 },
      },
    };
  }, []);

  // Web-only: detect supported MIME type
  const getMimeType = useCallback(() => {
    if (typeof MediaRecorder === "undefined") return null;
    if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
      return "audio/webm;codecs=opus";
    }
    if (MediaRecorder.isTypeSupported("audio/webm")) {
      return "audio/webm";
    }
    if (MediaRecorder.isTypeSupported("audio/mp4")) {
      return "audio/mp4";
    }
    return null;
  }, []);

  // Recording is supported on native (always) or web (if MediaRecorder exists)
  const isSupported =
    isNativePlatform() ||
    (typeof MediaRecorder !== "undefined" && getMimeType() !== null);

  // -------------------------------------------------------------------------
  // Timer
  // -------------------------------------------------------------------------
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    stopTimer();
    timerRef.current = setInterval(() => {
      const elapsed =
        (Date.now() - startTimeRef.current) / 1000 + pausedDurationRef.current;
      setRecordingDuration(elapsed);

      // Duration warning at 80%
      if (
        !warningFiredRef.current &&
        elapsed >= durationLimitSec * 0.8 &&
        onDurationWarning
      ) {
        warningFiredRef.current = true;
        onDurationWarning(durationLimitSec - elapsed);
      }

      // Auto-stop at limit
      if (elapsed >= durationLimitSec) {
        if (useNativeRef.current && nativeStopFnRef.current) {
          nativeStopFnRef.current();
        } else if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state === "recording"
        ) {
          mediaRecorderRef.current.stop();
        }
      }
    }, 100);
  }, [durationLimitSec, onDurationWarning, stopTimer]);

  // -------------------------------------------------------------------------
  // Native recording helpers
  // -------------------------------------------------------------------------
  const cleanupNative = useCallback(() => {
    if (nativeListenerRef.current) {
      nativeListenerRef.current.remove();
      nativeListenerRef.current = null;
    }
    nativeAmplitudeRef.current = 0;
    useNativeRef.current = false;
    nativeStopFnRef.current = null;
  }, []);

  // Convert native file path → Blob
  const nativeFileToBlob = async (filePath) => {
    const webUrl = window.Capacitor.convertFileSrc(filePath);
    // Use original browser fetch to avoid CapacitorHttp mangling
    const localFetch = window.CapacitorWebFetch || window.fetch;
    const response = await localFetch.call(window, webUrl);
    const raw = await response.blob();
    // Ensure correct MIME type
    return new Blob([raw], { type: "audio/mp4" });
  };

  // -------------------------------------------------------------------------
  // Start recording
  // -------------------------------------------------------------------------
  const startRecording = useCallback(async () => {
    setError(null);
    setAudioBlob(null);
    chunksRef.current = [];
    warningFiredRef.current = false;
    pausedDurationRef.current = 0;

    // ------ Native path (Android Capacitor) ------
    const nativeRecorder = getNativeRecorderPlugin();
    if (nativeRecorder) {
      try {
        useNativeRef.current = true;

        // Start native recording first — this is the call most likely to fail
        const startResult = await nativeRecorder.start();
        console.log("🎤 NATIVE recording started:", startResult);

        // Set up amplitude listener AFTER start succeeds
        try {
          nativeListenerRef.current = await nativeRecorder.addListener(
            "amplitudeUpdate",
            (data) => {
              nativeAmplitudeRef.current = data.amplitude || 0;
            },
          );
        } catch (listenerErr) {
          // Listener setup is non-critical — recording works without waveform
          console.warn(
            "🎤 Amplitude listener failed (waveform disabled):",
            listenerErr,
          );
        }

        // Define the stop handler once so auto-stop and manual stop share it
        nativeStopFnRef.current = async () => {
          try {
            const result = await nativeRecorder.stop();
            const blob = await nativeFileToBlob(result.filePath);
            setAudioBlob(blob);
            console.log("🎤 NATIVE recording saved, blob size:", blob.size);
          } catch (e) {
            console.error("❌ Error converting native recording:", e);
            setError("Failed to save recording: " + e.message);
          }
          setIsRecording(false);
          setIsPaused(false);
          stopTimer();
          cleanupNative();
        };

        startTimeRef.current = Date.now();
        setIsRecording(true);
        setIsPaused(false);
        setRecordingDuration(0);
        setPermissionGranted(true);
        startTimer();
        return;
      } catch (err) {
        // IMPORTANT: Show the error so we can debug — don't silently fall back
        const errMsg = "Native recorder failed: " + (err?.message || err);
        console.error("❌ " + errMsg);
        console.error("❌ Falling back to WebRTC path");
        cleanupNative();
        // Fall through to WebRTC path
      }
    }

    // ------ WebRTC path (web / fallback) ------
    try {
      await startAudioRouteEnforcement();

      let stream = streamRef.current;
      if (!stream) {
        const constraints = await getBuiltInMicConstraints();
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      }
      streamRef.current = stream;
      setPermissionGranted(true);

      // Set up Web Audio API with limiter for peak protection
      const audioContext = new (
        window.AudioContext || window.webkitAudioContext
      )();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);

      // Input gain — pull level down ~3 dB to give the compressor headroom
      const inputGain = audioContext.createGain();
      inputGain.gain.setValueAtTime(0.7, audioContext.currentTime);

      // DynamicsCompressor configured as a peak limiter —
      // transparent below threshold, catches transients before they clip
      const compressor = audioContext.createDynamicsCompressor();
      compressor.threshold.setValueAtTime(-12, audioContext.currentTime);
      compressor.knee.setValueAtTime(0, audioContext.currentTime);
      compressor.ratio.setValueAtTime(20, audioContext.currentTime);
      compressor.attack.setValueAtTime(0.001, audioContext.currentTime);
      compressor.release.setValueAtTime(0.15, audioContext.currentTime);

      // Soft clipper — a safety net after the compressor that gently
      // rounds off any remaining peaks instead of letting them hard-clip
      const softClipper = audioContext.createWaveShaper();
      const samples = 8192;
      const curve = new Float32Array(samples);
      for (let i = 0; i < samples; i++) {
        const x = (i * 2) / samples - 1;
        // tanh curve: transparent below ~0.5, gentle saturation above
        curve[i] = Math.tanh(x * 1.4);
      }
      softClipper.curve = curve;
      softClipper.oversample = "4x";

      // Route: mic → gain → compressor → soft clipper → recorder stream
      const destination = audioContext.createMediaStreamDestination();
      source.connect(inputGain);
      inputGain.connect(compressor);
      compressor.connect(softClipper);
      softClipper.connect(destination);

      // Analyser taps after processing — shows what's actually recorded
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      softClipper.connect(analyser);
      analyserRef.current = analyser;

      // Create MediaRecorder on the limiter-processed stream
      const mimeType = getMimeType();
      const recorder = new MediaRecorder(destination.stream, {
        mimeType,
        audioBitsPerSecond: 256000,
      });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        setIsRecording(false);
        setIsPaused(false);
        stopTimer();
      };

      recorder.onerror = (event) => {
        console.error("❌ MediaRecorder error:", event.error);
        setError(
          "Recording error: " + (event.error?.message || "Unknown error"),
        );
        setIsRecording(false);
        stopTimer();
      };

      recorder.start();
      startTimeRef.current = Date.now();
      setIsRecording(true);
      setIsPaused(false);
      setRecordingDuration(0);
      startTimer();

      console.log("🎤 Recording started (WebRTC), mimeType:", mimeType);
    } catch (err) {
      console.error("❌ Failed to start recording:", err);
      if (
        err.name === "NotAllowedError" ||
        err.name === "PermissionDeniedError"
      ) {
        setPermissionGranted(false);
        setError(
          "Microphone access denied. Please allow microphone access in your browser settings.",
        );
      } else if (err.name === "NotFoundError") {
        setError(
          "No microphone found. Please connect a microphone and try again.",
        );
      } else {
        setError("Failed to start recording: " + err.message);
      }
    }
  }, [
    getMimeType,
    getBuiltInMicConstraints,
    startTimer,
    stopTimer,
    cleanupNative,
  ]);

  // -------------------------------------------------------------------------
  // Pause recording
  // -------------------------------------------------------------------------
  const pauseRecording = useCallback(async () => {
    if (useNativeRef.current) {
      try {
        const nativeRecorder = getNativeRecorderPlugin();
        if (nativeRecorder) {
          await nativeRecorder.pause();
          setIsPaused(true);
          pausedDurationRef.current +=
            (Date.now() - startTimeRef.current) / 1000;
          stopTimer();
          console.log("⏸ Native recording paused");
        }
      } catch (e) {
        console.error("❌ Native pause failed:", e);
      }
      return;
    }

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      pausedDurationRef.current += (Date.now() - startTimeRef.current) / 1000;
      stopTimer();
      console.log("⏸ Recording paused");
    }
  }, [stopTimer]);

  // -------------------------------------------------------------------------
  // Resume recording
  // -------------------------------------------------------------------------
  const resumeRecording = useCallback(async () => {
    if (useNativeRef.current) {
      try {
        const nativeRecorder = getNativeRecorderPlugin();
        if (nativeRecorder) {
          await nativeRecorder.resume();
          setIsPaused(false);
          startTimeRef.current = Date.now();
          startTimer();
          console.log("▶ Native recording resumed");
        }
      } catch (e) {
        console.error("❌ Native resume failed:", e);
      }
      return;
    }

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "paused"
    ) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      startTimeRef.current = Date.now();
      startTimer();
      console.log("▶ Recording resumed");
    }
  }, [startTimer]);

  // -------------------------------------------------------------------------
  // Stop recording (saves the blob)
  // -------------------------------------------------------------------------
  const stopRecording = useCallback(async () => {
    if (useNativeRef.current && nativeStopFnRef.current) {
      await nativeStopFnRef.current();
      console.log("⏹ Native recording stopped");
      return;
    }

    if (
      mediaRecorderRef.current &&
      (mediaRecorderRef.current.state === "recording" ||
        mediaRecorderRef.current.state === "paused")
    ) {
      mediaRecorderRef.current.stop();
      console.log("⏹ Recording stopped");
    }

    // Release mic stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
      analyserRef.current = null;
    }

    // Stop enforcing audio routing — let Android manage normally again
    stopAudioRouteEnforcement();
  }, []);

  // -------------------------------------------------------------------------
  // Cancel recording (discard without saving)
  // -------------------------------------------------------------------------
  const cancelRecording = useCallback(async () => {
    if (useNativeRef.current) {
      try {
        const nativeRecorder = getNativeRecorderPlugin();
        if (nativeRecorder) {
          await nativeRecorder.cancel();
        }
      } catch (e) {
        // Ignore
      }
      setIsRecording(false);
      setIsPaused(false);
      setRecordingDuration(0);
      setAudioBlob(null);
      stopTimer();
      cleanupNative();
      console.log("✕ Native recording cancelled");
      return;
    }

    chunksRef.current = [];

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      // Override onstop to prevent blob creation
      mediaRecorderRef.current.onstop = () => {
        setIsRecording(false);
        setIsPaused(false);
        setRecordingDuration(0);
        setAudioBlob(null);
      };
      mediaRecorderRef.current.stop();
    }

    // Release mic stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
      analyserRef.current = null;
    }

    stopAudioRouteEnforcement();
    stopTimer();
    console.log("✕ Recording cancelled");
  }, [stopTimer, cleanupNative]);

  // -------------------------------------------------------------------------
  // Waveform visualization data
  // -------------------------------------------------------------------------
  const getAnalyserData = useCallback(() => {
    // Native path: synthesize waveform from amplitude events
    if (useNativeRef.current) {
      const amp = nativeAmplitudeRef.current;
      const bufferLength = 128;
      const dataArray = new Uint8Array(bufferLength);
      const now = Date.now() * 0.008;
      for (let i = 0; i < bufferLength; i++) {
        const phase = (i / bufferLength) * Math.PI * 6 + now;
        const wave = Math.sin(phase) * amp * 127;
        dataArray[i] = Math.round(128 + wave);
      }
      return dataArray;
    }

    // WebRTC path: read from AnalyserNode
    if (!analyserRef.current) return null;
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteTimeDomainData(dataArray);
    return dataArray;
  }, []);

  const getFrequencyData = useCallback(() => {
    // Native path: synthesize frequency-like data from amplitude
    if (useNativeRef.current) {
      const amp = nativeAmplitudeRef.current;
      const bufferLength = 128;
      const dataArray = new Uint8Array(bufferLength);
      for (let i = 0; i < bufferLength; i++) {
        const falloff = 1 - i / bufferLength;
        dataArray[i] = Math.round(amp * falloff * 255);
      }
      return dataArray;
    }

    if (!analyserRef.current) return null;
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);
    return dataArray;
  }, []);

  // -------------------------------------------------------------------------
  // Pre-request mic permission
  // -------------------------------------------------------------------------
  const requestMicPermission = useCallback(async () => {
    // Native path: use the native plugin's permission system
    const nativeRecorder = getNativeRecorderPlugin();
    if (nativeRecorder) {
      try {
        const status = await nativeRecorder.checkPermissions();
        if (status?.microphone === "granted") {
          setPermissionGranted(true);
          return;
        }
        const result = await nativeRecorder.requestPermissions();
        setPermissionGranted(result?.microphone === "granted");
        if (result?.microphone !== "granted") {
          throw Object.assign(new Error("Microphone permission denied"), {
            name: "NotAllowedError",
          });
        }
      } catch (err) {
        if (
          err.name === "NotAllowedError" ||
          err.name === "PermissionDeniedError"
        ) {
          setPermissionGranted(false);
        }
        throw err;
      }
      return;
    }

    // Web path: use getUserMedia to pre-warm
    if (streamRef.current) return;
    try {
      const constraints = await getBuiltInMicConstraints();
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      startAudioRouteEnforcement().then(() => stopAudioRouteEnforcement());
      streamRef.current = stream;
      setPermissionGranted(true);
    } catch (err) {
      if (
        err.name === "NotAllowedError" ||
        err.name === "PermissionDeniedError"
      ) {
        setPermissionGranted(false);
      }
      throw err;
    }
  }, [getBuiltInMicConstraints]);

  // Reset state for a fresh recording
  const resetRecording = useCallback(() => {
    setAudioBlob(null);
    setRecordingDuration(0);
    setError(null);
    warningFiredRef.current = false;
    pausedDurationRef.current = 0;
  }, []);

  // -------------------------------------------------------------------------
  // Cleanup on unmount
  // -------------------------------------------------------------------------
  useEffect(() => {
    return () => {
      stopTimer();
      stopAudioRouteEnforcement();

      // Clean up native listener
      if (nativeListenerRef.current) {
        nativeListenerRef.current.remove();
        nativeListenerRef.current = null;
      }
      // Cancel native recording if still running
      if (useNativeRef.current) {
        const nativeRecorder = getNativeRecorderPlugin();
        if (nativeRecorder) {
          nativeRecorder.cancel().catch(() => {});
        }
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, [stopTimer]);

  return {
    // State
    isRecording,
    isPaused,
    recordingDuration,
    audioBlob,
    error,
    isSupported,
    permissionGranted,

    // Controls
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
    resetRecording,
    requestMicPermission,

    // Visualization
    getAnalyserData,
    getFrequencyData,
    analyserRef,

    // Config
    durationLimitSec,
  };
};

export default useAudioRecorder;
